// src/Organizer/Auth/OnboardingRealtime.jsx
import React, { useEffect, useRef, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AudioConfig, SpeechConfig, SpeechRecognizer, AudioInputStream, ResultReason } from 'microsoft-cognitiveservices-speech-sdk';
import { User } from 'lucide-react';
import BrandingSetup from '../Dashboard/BrandingSetup';
import WebsiteInput from './WebsiteInput';

// Azure Speech Configuration
const API_KEY = process.env.REACT_APP_COG_SERVICE_KEY;
const API_LOCATION = process.env.REACT_APP_COG_SERVICE_LOCATION;
const speechConfig = API_KEY && API_LOCATION ? SpeechConfig.fromSubscription(API_KEY, API_LOCATION) : null;

// Step definitions
const STEPS = [
  { id: 'welcome', label: 'Welcome', number: 1 },
  { id: 'basic_info', label: 'Basic Information', number: 2 },
  { id: 'collect_website', label: 'Collect Website', number: 3 },
  { id: 'brand_setup', label: 'Persona Brand Setup', number: 4 },
  { id: 'voice_creation', label: 'Persona Voice Creation', number: 5 },
  { id: 'send_emails', label: 'Send Welcome Emails', number: 6 },
];

export function OnboardingRealtime() {
  const location = useLocation();
  const navigate = useNavigate();

  const [aiCalled, setAICalled] = useState(false);
  const [currentStep, setCurrentStep] = useState('welcome');
  const [extractedBranding, setExtractedBranding] = useState(null);
  const [completionMessage, setCompletionMessage] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  const [textInputValue, setTextInputValue] = useState("");

  const persona = location.state?.persona;
  const organizerId = location.state?.organizerId;
  const organizerName = location.state?.organizerName || localStorage.getItem('organizer_name') || 'there';

  // Refs & internal state
  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const audioElRef = useRef(null);

  // Azure STT refs
  const recognizerRef = useRef(null);
  const recognitionActiveRef = useRef(false);
  const audioStreamRef = useRef(null);

  const conversationRef = useRef([]);
  const processedResponseIdsRef = useRef(new Set());
  const currentAIResponseRef = useRef("");
  const fullTranscriptRef = useRef("");
  const onboardingSummaryRef = useRef(null);
  const chatContainerRef = useRef(null);

  // user/a.i. streaming control
  const onboardingSavedRef = useRef(false);
  const initialPromptSentRef = useRef(false);
  const onboardingStartedRef = useRef(false);
  const completionDetectedRef = useRef(false);
  const stopSessionCalledRef = useRef(false);

  // queue/flags
  const pendingUserAnswerRef = useRef(null);
  const isAIRespondingRef = useRef(false);

  // Last user dedupe
  const lastUserTextRef = useRef("");
  const lastUserTsRef = useRef(0);

  // UI state
  const [convTick, setConvTick] = useState(0);
  const [status, setStatus] = useState("idle");
  const [transcript, setTranscript] = useState(""); // assistant streaming interim
  const [liveUserTranscript, setLiveUserTranscript] = useState(""); // user interim
  const [error, setError] = useState(null);
  const [isRecognising, setIsRecognising] = useState(false);
  const [recognisingText, setRecognisingText] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  
  const hardStoppedRef = useRef(false);

  // Validate required props
  useEffect(() => {
    if (!persona || !organizerId) {
      navigate("/organizer/register");
    }
  }, [persona, organizerId, navigate]);

  // Auto-scroll to bottom when conversation updates
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTo({
        top: chatContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [convTick, liveUserTranscript, transcript, status]);

  // Note: All speech is handled by real-time AI, no browser TTS

  // Start onboarding session
  useEffect(() => {
    if (!persona || !organizerId) return;
    if (onboardingStartedRef.current) return;

    onboardingStartedRef.current = true;
    startOnboarding();

    return () => {
      // Cleanup: stop session if not already stopped
      if (!stopSessionCalledRef.current) {
        stopSession();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona, organizerId]);

  /* ---------------------------
     START / STOP ONBOARDING (WebRTC)
     --------------------------- */
  async function startOnboarding() {
    try {
      setStatus("connecting");
      setError(null);

      if (aiCalled) return;
      setAICalled(true);

      const resp = await fetch("/api/onboarding-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          persona,
          organizer_id: organizerId
        }),
      });

      if (!resp.ok) {
        const error = await resp.json().catch(() => ({}));
        throw new Error(error.detail || "Failed to start onboarding session");
      }

      const json = await resp.json();
      const EPHEMERAL_KEY =
        json?.session?.client_secret?.value ??
        json?.session?.client_secret ??
        json?.session?.token ??
        json?.client_secret?.value ??
        json?.client_secret ??
        null;

      if (!EPHEMERAL_KEY) {
        throw new Error("No session key returned from server");
      }

      // Start Azure STT initialization in parallel — non-blocking
      initializeAzureSTT().catch(err => {
        console.warn("[Azure] STT init failed:", err);
      });

      await startWebRTC(EPHEMERAL_KEY);

    } catch (err) {
      console.error("[onboarding] startOnboarding error", err);
      setError(err.message);
      setStatus("error");
    }
  }

  async function startWebRTC(ephemeralKey) {
    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      // Create audio element
      const audioEl = document.createElement("audio");
      audioEl.autoplay = true;
      audioElRef.current = audioEl;

      // Audio event handlers
      audioEl.addEventListener("playing", () => {
        if (!onboardingSavedRef.current) {
          setStatus("speaking");
        }
      });

      audioEl.addEventListener("ended", () => {
        // If completion was detected and audio ended, proceed
        if (completionDetectedRef.current && !onboardingSavedRef.current) {
          setTimeout(() => handleCompletion(), 500);
        } else if (!onboardingSavedRef.current) {
          setStatus("listening");
        }
      });

      // Handle incoming audio track
      pc.ontrack = (ev) => {
        if (ev.streams && ev.streams[0]) {
          audioEl.srcObject = ev.streams[0];
        }
      };

      // Create data channel
      const dc = pc.createDataChannel("oai-events");
      dcRef.current = dc;

      dc.onopen = () => {
        console.log("[onboarding] Data channel opened");
        setStatus("thinking");

        // If there's a user answer queued, send it now
        if (pendingUserAnswerRef.current) {
          sendUserAnswerToRealtime(pendingUserAnswerRef.current);
          pendingUserAnswerRef.current = null;
        }

        // Small delay before sending initial prompt
        setTimeout(() => {
          sendInitialPrompt();
        }, 400);
      };

      dc.onmessage = (ev) => {
        try {
          handleRealtimeMessage(ev.data);
        } catch (err) {
          console.error("[onboarding] Error handling message:", err);
        }
      };

      dc.onerror = (err) => {
        console.error("[onboarding] Data channel error:", err);
        setStatus("error");
      };

      dc.onclose = () => {
        console.log("[onboarding] Data channel closed");
      };

      // Add local audio track (microphone)
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        });
        stream.getTracks().forEach(track => {
          pc.addTrack(track, stream);
        });
      } catch (err) {
        console.warn("[onboarding] Microphone access failed:", err);
        // Continue without microphone
      }

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const model = encodeURIComponent("gpt-4o-mini-realtime-preview");
      const sdpResp = await fetch(`https://api.openai.com/v1/realtime?model=${model}`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });

      if (!sdpResp.ok) {
        const errorText = await sdpResp.text();
        throw new Error(`SDP exchange failed: ${sdpResp.status} ${errorText}`);
      }

      const answer = await sdpResp.text();
      await pc.setRemoteDescription({ type: "answer", sdp: answer });

      console.log("[onboarding] WebRTC session established");

    } catch (err) {
      console.error("[onboarding] WebRTC setup failed:", err);
      setError(err.message);
      setStatus("error");
    }
  }

  /* ---------------------------
     Realtime message handling
     --------------------------- */

  function handleRealtimeMessage(dataRaw) {
    const text = typeof dataRaw === 'string' ? dataRaw : '';
    if (!text) return;

    let data;
    try {
      data = JSON.parse(text);
    } catch (e) {
      // Not JSON, might be plain text
      handlePlainTextMessage(text, { inferredType: null, isUser: false });
      return;
    }

    const type = data.type || '';

    // Detect AI responding state
    if (type === 'response.created' || type === 'response.output_item.added') {
      isAIRespondingRef.current = true;
      currentAIResponseRef.current = "";
      setStatus("speaking");
    }

    switch (type) {
      case 'response.audio_transcript.delta':
      case 'response.output_text.delta':
        handleDeltaMessage(data, { isUser: false, type });
        break;

      case 'response.audio_transcript.done':
      case 'response.done':
      case 'response.completed':
        isAIRespondingRef.current = false;
        handleResponseDone(data);
        break;

      // Some providers may emit input transcript events — treat them as user
      case 'input.audio_transcript.delta':
      case 'input.transcript.delta':
      case 'response.input_transcript.delta':
        handleDeltaMessage(data, { isUser: true, type });
        break;

      case 'error':
        console.error("[onboarding] AI error:", data.error);
        setStatus("error");
        break;

      default:
        // Try to extract text from other message types
        const extracted = extractTextFromMessage(data);
        if (extracted) {
          // heuristics: if message has author:user, treat as user
          const isUser = Boolean(data?.author === 'user' || data?.participant === 'user' || /input/i.test(type));
          handlePlainTextMessage(extracted, { inferredType: type, isUser });
        }
    }
  }

  function normalizeText(s) {
    if (!s) return '';
    return String(s).replace(/\s+/g, ' ').trim();
  }

  function isUserMessageType(typeStr) {
    if (!typeStr) return false;
    const t = String(typeStr).toLowerCase();
    return t.includes('input') || t.includes('user') || t.includes('speech_to_text') || t.includes('audio_transcript_for_input');
  }

  function handleDeltaMessage(data, meta = {}) {
    const deltaText = normalizeText(data.delta || data.transcript || data.text || '');
    if (!deltaText) return;

    const isUser = !!meta.isUser || isUserMessageType(meta.type);

    if (isUser) {
      // interim user transcript
      setLiveUserTranscript(prev => {
        const merged = (prev ? prev + " " : "") + deltaText;
        return normalizeText(merged);
      });
      setRecognisingText(deltaText);
      setIsRecognising(true);

      // add to full transcript prefixed
      fullTranscriptRef.current += `[USER-INTERIM] ${deltaText}\n`;
    } else {
      // assistant interim
      currentAIResponseRef.current += deltaText;
      setTranscript(prev => prev + deltaText);
      fullTranscriptRef.current += `[AI-INTERIM] ${deltaText}\n`;
    }

    // Check for completion on the combined transcript
    if (!completionDetectedRef.current) {
      const isComplete = checkForCompletion(fullTranscriptRef.current + " " + deltaText);
      if (isComplete) {
        if (!hardStoppedRef.current) {
          hardStoppedRef.current = true;
          completionDetectedRef.current = true;
          stopEverythingAndSave();
        }
      }
    }
  }

  function handleResponseDone(data) {
    const finalText = normalizeText(data.transcript || currentAIResponseRef.current || '');
    const responseId = data.response_id || data.response?.id || `msg-${Date.now()}`;

    if (finalText && !processedResponseIdsRef.current.has(responseId)) {
      processedResponseIdsRef.current.add(responseId);

      // push to conversation
      conversationRef.current = [
        ...conversationRef.current,
        {
          role: "assistant",
          text: finalText,
          id: responseId,
          timestamp: Date.now()
        }
      ];
      setConvTick(t => t + 1);

      // append to full transcript
      fullTranscriptRef.current += `[AI] ${finalText}\n`;

      // check completion
      const isComplete = checkForCompletion(finalText);
      if (isComplete) {
        if (!hardStoppedRef.current) {
          hardStoppedRef.current = true;
          completionDetectedRef.current = true;
          stopEverythingAndSave();
        }
      }
    }

    // clear assistant interim buffer
    currentAIResponseRef.current = "";

    if (!completionDetectedRef.current && !onboardingSavedRef.current) {
      setStatus("listening");
    }
  }

  function handlePlainTextMessage(text, meta = {}) {
    const t = normalizeText(text);
    if (!t) return;

    const isUser = meta.isUser || isUserMessageType(meta.inferredType) || false;

    if (isUser) {
      // final user message -> push to conversation and fullTranscript and send to AI
      const now = Date.now();

      // dedupe a little
      if (lastUserTextRef.current === t && (now - (lastUserTsRef.current || 0) < 2500)) {
        return;
      }
      lastUserTextRef.current = t;
      lastUserTsRef.current = now;

      const newMessage = {
        role: 'user',
        text: t,
        id: `user-${now}`,
        timestamp: now
      };
      conversationRef.current = [...conversationRef.current, newMessage];
      setConvTick(c => c + 1);

      // append to full transcript
      fullTranscriptRef.current += `[USER] ${t}\n`;

      // send to AI
      sendUserAnswerToRealtime(t);
    } else {
      // assistant final text fallback
      conversationRef.current = [
        ...conversationRef.current,
        {
          role: "assistant",
          text: t,
          id: `ai-${Date.now()}`,
          timestamp: Date.now()
        }
      ];
      setConvTick(tk => tk + 1);
      fullTranscriptRef.current += `[AI] ${t}\n`;
    }

    // If this text contains completion phrase, trigger completion
    if (!completionDetectedRef.current) {
      const isComplete = checkForCompletion(t);
      if (isComplete) {
        completionDetectedRef.current = true;
        console.log("[onboarding] Completion detected in plain text");
        setTimeout(() => {
          if (!onboardingSavedRef.current) stopEverythingAndSave();
        }, 600);
      }
    }
  }

  /* ---------------------------
     Completion detection + save flow
     --------------------------- */

  function checkForCompletion(text) {
    if (!text) return false;

    const normalized = text.toLowerCase();
    const completionPhrases = [
      "perfect — your preferences have been saved.",
      "great — your preferences are saved.",
      "done — your preferences are saved.",
      "all set — onboarding completed.",
      "perfect! your preferences have been saved",
      "your preferences have been saved",
      "your peference has been saved",
      "onboarding complete",
      "onboarding completed",
      "setup complete",
      "you're all set",
      "all set",
      "onboarding finished"
    ];
    
    return completionPhrases.some(phrase => normalized.includes(phrase));
  }

  async function stopRealtimeSession() {
    try {
      // clearUserSilenceTimer();
      if (pcRef.current) {
        try { 
          pcRef.current.getSenders().forEach(s => s.track?.stop()); 
        } catch (e) {}
        pcRef.current.close();
      }
    } catch (e) {}
    pcRef.current = null;
    dcRef.current = null;
  }

async function stopEverythingAndSave() {
  hardStoppedRef.current = true;

  console.log("[onboarding] HARD STOP initiated");

  try {
    // 1️⃣ Stop Azure STT COMPLETELY
    await cleanupRecognizer();

    // 2️⃣ Close WebRTC + DataChannel

    // 3️⃣ Prevent any queued messages
    pendingUserAnswerRef.current = null;
    isAIRespondingRef.current = false;

    // 4️⃣ Proceed to save
    handleCompletion();
  } catch (err) {
    console.warn("[onboarding] Hard stop error", err);
    handleCompletion();
  }
}

  // Step detection from conversation
  useEffect(() => {
    if (conversationRef.current.length === 0) return;

    const lastMessage = conversationRef.current[conversationRef.current.length - 1];
    if (lastMessage.role === 'assistant') {
      const text = lastMessage.text.toLowerCase();
      
      // Detect step transitions from AI messages
      if ((text.includes('website') || text.includes('url') || text.includes('link') || text.includes('site')) && currentStep === 'basic_info') {
        // Don't auto-transition, let completion handler do it
      } else if ((text.includes('brand') || text.includes('logo') || text.includes('color') || text.includes('customize')) && currentStep === 'collect_website') {
        // Transition happens after website collection
      } else if ((text.includes('voice') || text.includes('audio') || text.includes('speech')) && currentStep === 'brand_setup') {
        setCurrentStep('voice_creation');
      } else if ((text.includes('email') || text.includes('welcome message')) && currentStep === 'voice_creation') {
        setCurrentStep('send_emails');
      }
    }
  }, [convTick, currentStep]);

  // Detect when basic info step is complete
  useEffect(() => {
    if (completionDetectedRef.current && currentStep === 'basic_info') {
      setTimeout(() => {
        setCurrentStep('collect_website');
      }, 1000);
    }
  }, [currentStep]);

  const handleCompletion = useCallback(async () => {
    if (onboardingSavedRef.current) return;

    onboardingSavedRef.current = true;
    setStatus("saving");

    // Extract question-answer pairs from conversation, filtering out greetings
    const questionAnswerPairs = [];
    const greetingKeywords = ['hello', 'hi', 'hey', 'welcome', 'good morning', 'good afternoon', 'good evening', 'nice to meet', 'pleasure to meet', 'great to have you', 'thanks for joining'];
    
    for (let i = 0; i < conversationRef.current.length; i++) {
      const msg = conversationRef.current[i];
      
      // Find AI questions followed by user answers
      if (msg.role === 'assistant' && i + 1 < conversationRef.current.length) {
        const nextMsg = conversationRef.current[i + 1];
        
        if (nextMsg.role === 'user') {
          // Check if this is not a greeting message
          const msgLower = msg.text.toLowerCase();
          const isGreeting = greetingKeywords.some(keyword => msgLower.includes(keyword)) && msgLower.length < 200;
          
          if (!isGreeting) {
            questionAnswerPairs.push({
              id: nextMsg.id || `answer-${questionAnswerPairs.length}`,
              question: msg.text,
              answer: nextMsg.text,
              originalAnswer: nextMsg.text,
              timestamp: nextMsg.timestamp
            });
          }
        }
      }
    }

    setStatus("saving");
    
    // Save directly without confirmation modal
    const saveData = {
      organizer_id: organizerId,
      persona: persona,
      conversation: conversationRef.current,
    };

    try {
      console.log("[onboarding] Saving data:", saveData);

      await fetch(`/api/organizer/${encodeURIComponent(organizerId)}/onboarding`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(saveData),
      });
      
      // Close session and move to next step
      await stopRealtimeSession();
      await stopSession();
      setStatus("done");
      // Small delay for smooth transition
      setTimeout(() => {
        setCurrentStep('collect_website');
      }, 800);

    } catch (err) {
      console.error("[onboarding] Save error:", err);
      setError("Failed to save preferences. Please try again.");
      setStatus("error");

      // still move to next step fallback
      setTimeout(() => {
        setCurrentStep('collect_website');
      }, 1200);
    }
  }, [organizerId, persona]);


  /* ---------------------------
     stopSession (also stops Azure)
     --------------------------- */
  async function stopSession() {
    if (stopSessionCalledRef.current) return;
    stopSessionCalledRef.current = true;

    console.log("[onboarding] Stopping session...");

    // Close data channel
    if (dcRef.current) {
      try {
        dcRef.current.close();
      } catch (e) { /* ignore */ }
      dcRef.current = null;
    }

    // Close peer connection
    if (pcRef.current) {
      try {
        pcRef.current.getSenders().forEach(sender => {
          if (sender.track) sender.track.stop();
        });
        pcRef.current.close();
      } catch (e) { /* ignore */ }
      pcRef.current = null;
    }

    // Clean up audio
    if (audioElRef.current) {
      try {
        audioElRef.current.pause();
        audioElRef.current.srcObject = null;
      } catch (e) { /* ignore */ }
      audioElRef.current = null;
    }

    // Stop azure recognition too
    try {
      await cleanupRecognizer();
    } catch (_) {}
  }

  /* ---------------------------
     Azure STT helpers (start/stop/cleanup)
     --------------------------- */

  async function startAzureRecognition() {
    if (!recognizerRef.current) {
      console.error("[Azure] Recognizer not initialized");
      setErrorMessage("Recognizer not initialized");
      return;
    }
    if (recognitionActiveRef.current) return;

    recognitionActiveRef.current = true;
    try {
      recognizerRef.current.startContinuousRecognitionAsync(
        () => {
          setIsRecognising(true);
          setRecognisingText("");
          setErrorMessage("");
          console.log('[Azure] Recognition started');
        },
        (err) => {
          setErrorMessage(String(err));
          recognitionActiveRef.current = false;
          setIsRecognising(false);
          console.error('[Azure] Start error:', err);
        }
      );
    } catch (err) {
      console.error("[Azure] startAzureRecognition error", err);
      setErrorMessage(String(err));
      recognitionActiveRef.current = false;
      setIsRecognising(false);
    }
  }

  async function stopAzureRecognition() {
    if (!recognizerRef.current || !recognitionActiveRef.current) return;
    recognitionActiveRef.current = false;
    return new Promise((resolve) => {
      recognizerRef.current.stopContinuousRecognitionAsync(
        () => {
          setIsRecognising(false);
          setRecognisingText("");
          setLiveUserTranscript("");
          console.log('[Azure] Recognition stopped');
          resolve();
        },
        (err) => {
          console.warn("[Azure] stop error", err);
          setIsRecognising(false);
          resolve();
        }
      );
    });
  }

  const cleanupRecognizer = async () => {
    if (recognizerRef.current) {
      recognitionActiveRef.current = false;
      try { await stopAzureRecognition(); } catch (_) {}
      try { recognizerRef.current.close(); } catch (_) {}
      recognizerRef.current = null;
    }
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach(t => t.stop());
      audioStreamRef.current = null;
    }
  };

  // Initialize Azure STT
  async function initializeAzureSTT() {
    try {
      // create/get microphone stream
      const constraints = { audio: true, video: false };
      const audioStream = await navigator.mediaDevices.getUserMedia(constraints);
      audioStreamRef.current = audioStream;

      // create recognizer
      let recognizer;
      try {
        // Preferred: from default mic config
        const ac = AudioConfig.fromDefaultMicrophoneInput();
        // Note: you must create `speechConfig` in this module; otherwise instantiate here
        if (!speechConfig) {
          console.error("[Azure] speechConfig not found; please set REACT_APP_COG_SERVICE_KEY and REACT_APP_COG_SERVICE_LOCATION environment variables");
          throw new Error("Azure Speech configuration is missing");
        }
        recognizer = new SpeechRecognizer(speechConfig, ac);
      } catch (err) {
        // fallback to push stream (less common in browser)
        if (!speechConfig) {
          console.error("[Azure] speechConfig not found; please set REACT_APP_COG_SERVICE_KEY and REACT_APP_COG_SERVICE_LOCATION environment variables");
          throw new Error("Azure Speech configuration is missing");
        }
        const pushStream = AudioInputStream.createPushStream();
        const ac = AudioConfig.fromStreamInput(pushStream);
        recognizer = new SpeechRecognizer(speechConfig, ac);
      }

      // Interim transcripts
      recognizer.recognizing = (s, e) => {
        const interimText = e.result?.text ?? "";
        // show live user interim
        setRecognisingText(interimText);
        setLiveUserTranscript(interimText);
        setIsRecognising(true);

        // track last user speech
        // not used for now, but good to have
      };

      // Final recognized text -> push to conversation and send to AI
      recognizer.recognized = (s, e) => {
        const r = e.result;
        if (!r) return;

        if (r.reason === ResultReason.RecognizedSpeech && r.text) {
          const newText = r.text.trim();
          if (!newText) return;

          const now = Date.now();
          // dedupe
          if (lastUserTextRef.current === newText && (now - (lastUserTsRef.current || 0) < 3000)) {
            // ignore duplicate
            setLiveUserTranscript("");
            setRecognisingText("");
            return;
          }
          lastUserTextRef.current = newText;
          lastUserTsRef.current = now;

          // clear live interim
          setLiveUserTranscript("");
          setRecognisingText("");

          // push final user message
          const newMessage = {
            role: 'user',
            text: newText,
            id: `user-${now}`,
            timestamp: now
          };
          conversationRef.current = [...conversationRef.current, newMessage];
          setConvTick(t => t + 1);

          // append to transcript for saving
          fullTranscriptRef.current += `[USER] ${newText}\n`;

          // send to AI (queue if dc not open)
          sendUserAnswerToRealtime(newText);
        }
      };

      recognizer.canceled = (s, e) => {
        let msg = `Canceled: ${e.reason}`;
        if (e.errorDetails) msg += ` - ${e.errorDetails}`;
        console.warn("[Azure] canceled:", msg);
        setErrorMessage(msg);
        recognitionActiveRef.current = false;
        setIsRecognising(false);
        if (!completionDetectedRef.current && !hardStoppedRef.current) {
          setTimeout(startAzureRecognition, 700);
        }

      };

      recognizer.sessionStopped = (s, e) => {
        console.log('[Azure] Session stopped');
        recognitionActiveRef.current = false;
        setIsRecognising(false);
        setTimeout(() => {
          if (!completionDetectedRef.current) startAzureRecognition();
        }, 500);
      };

      recognizerRef.current = recognizer;
      await startAzureRecognition();
    } catch (err) {
      console.error("[Azure] Error initializing STT:", err);
      setErrorMessage(String(err));
    }
  }

  /* ---------------------------
     Send user text to realtime model
     --------------------------- */
function sendUserAnswerToRealtime(text) {
  if (!text || !text.trim()) return;

  if (
    hardStoppedRef.current ||
    completionDetectedRef.current ||
    onboardingSavedRef.current
  ) {
    return;
  }

  // If data channel not ready, queue
  if (!dcRef.current || dcRef.current.readyState !== 'open') {
    pendingUserAnswerRef.current = text;
    console.debug('[onboarding] DC not open, queued user text');
    return;
  }

  const payload = {
    type: "response.create",
    response: {
      // ✅ MUST BE ARRAY
      input: [
        {
          role: "user",
          content: [
            { type: "input_text", text }
          ]
        }
      ],
      modalities: ["text", "audio"]
    }
  };

  try {
    isAIRespondingRef.current = true;
    setStatus("thinking");
    safeSendDC(payload);
    // Hide text input after sending
    setShowTextInput(false);
    setTextInputValue("");
  } catch (err) {
    console.warn("[onboarding] sendUserAnswerToRealtime failed", err);
    pendingUserAnswerRef.current = text;
  }
}

  // Handle text input submission
  const handleTextInputSubmit = (e) => {
    e.preventDefault();
    if (textInputValue.trim()) {
      sendUserAnswerToRealtime(textInputValue.trim());
    }
  };

  // Detect when text input should be shown
  useEffect(() => {
    if (conversationRef.current.length === 0) return;
    
    const lastMessage = conversationRef.current[conversationRef.current.length - 1];
    if (lastMessage.role === 'assistant' && currentStep === 'basic_info') {
      const text = lastMessage.text.toLowerCase();
      // Show text input if AI asks a question that might need text response
      const needsTextInput = text.includes('?') && (
        text.includes('name') || 
        text.includes('email') || 
        text.includes('company') ||
        text.includes('type') ||
        text.includes('enter') ||
        text.includes('write') ||
        text.includes('text')
      );
      
      // Also check if user is not speaking (no live transcript)
      if (needsTextInput && !liveUserTranscript && status === 'listening') {
        setShowTextInput(true);
      }
    } else {
      setShowTextInput(false);
    }
  }, [convTick, currentStep, liveUserTranscript, status]);


  /* ---------------------------
     Utilities: extract text from generic message
     --------------------------- */
  function extractTextFromMessage(data) {
    if (!data) return '';

    if (typeof data.text === 'string' && data.text.trim()) return data.text;
    if (typeof data.transcript === 'string' && data.transcript.trim()) return data.transcript;
    if (typeof data.delta === 'string' && data.delta.trim()) return data.delta;
    if (typeof data.content === 'string' && data.content.trim()) return data.content;
    if (typeof data.message === 'string' && data.message.trim()) return data.message;

    if (Array.isArray(data.content)) {
      return data.content
        .filter(item => (item.type === 'output_text' || item.type === 'text' || item.type === 'input_text') && item.text)
        .map(item => item.text)
        .join(' ');
    }

    return '';
  }

  /* ---------------------------
     sendInitialPrompt + safe DC send
     --------------------------- */
  function sendInitialPrompt() {
    if (initialPromptSentRef.current) return;
    initialPromptSentRef.current = true;

    // Get persona display name
    const personaNames = {
      'founder': 'Founder/CEO',
      'investor': 'Investor/VC',
      'recruiter': 'Recruiter',
      'hr_manager': 'HR Manager',
      'vendor': 'Vendor Evaluator'
    };
    const personaDisplayName = personaNames[persona] || 'Professional';

    // Send initial greeting instruction with user's name and persona context
    const payload = {
      type: "response.create",
      response: {
        input: [
          {
            type: "message",
            role: "user",
            content: [
              { 
                type: "input_text", 
                text: `My name is ${organizerName} and I've selected the ${personaDisplayName} persona type. Please start the greeting flow and make sure to explain what we're going to do together - creating a personalized AI persona for me.`
              }
            ]
          }
        ],
        modalities: ["text", "audio"],
      },
    };

    safeSendDC(payload);
  }

  function safeSendDC(payload) {
    if (!dcRef.current) return;
    if (dcRef.current.readyState !== 'open') {
      setTimeout(() => safeSendDC(payload), 120);
      return;
    }
    try {
      dcRef.current.send(JSON.stringify(payload));
    } catch (err) {
      console.warn("[onboarding] Failed to send DC message:", err);
    }
  }

  /* ---------------------------
     UI render
     --------------------------- */

  // Calculate progress percentage
  const getProgressPercentage = () => {
    const currentIndex = STEPS.findIndex(s => s.id === currentStep);
    return Math.round(((currentIndex + 1) / STEPS.length) * 100);
  };

  // Render UI
  if (!persona || !organizerId) {
    return <div>Loading...</div>;
  }

  const currentStepIndex = STEPS.findIndex(s => s.id === currentStep);
  const currentStepInfo = STEPS[currentStepIndex] || STEPS[0];

  return (
    <>
      <style>
        {`
          @keyframes messageSlideIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          @keyframes typingAnimation {
            0%, 60%, 100% {
              transform: translateY(0);
              opacity: 0.7;
            }
            30% {
              transform: translateY(-10px);
              opacity: 1;
            }
          }

          @keyframes pulse {
            0%, 100% {
              opacity: 1;
              transform: scale(1);
            }
            50% {
              opacity: 0.6;
              transform: scale(1.1);
            }
          }

          @keyframes bounce {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-5px);
            }
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }

          @keyframes slideIn {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          /* Custom scrollbar */
          .conversation-panel-scroll::-webkit-scrollbar,
          .sidebar-scroll::-webkit-scrollbar {
            width: 4px;
          }

          .conversation-panel-scroll::-webkit-scrollbar-track,
          .sidebar-scroll::-webkit-scrollbar-track {
            background: transparent;
          }

          .conversation-panel-scroll::-webkit-scrollbar-thumb,
          .sidebar-scroll::-webkit-scrollbar-thumb {
            background: rgba(255, 255, 255, 0.05);
            border-radius: 10px;
          }

          .conversation-panel-scroll::-webkit-scrollbar-thumb:hover,
          .sidebar-scroll::-webkit-scrollbar-thumb:hover {
            background: rgba(255, 255, 255, 0.2);
          }

          /* BrandingSetup inline styles */
          .branding-setup-inline {
            background: transparent !important;
            padding: 0 !important;
            box-shadow: none !important;
          }

          .branding-setup-inline > h2 {
            display: none !important;
          }

          .branding-setup-inline > .subtitle {
            display: none !important;
          }

          .website-input-inline {
            padding: 0 !important;
          }

          .website-input-inline .website-input-card {
            background: transparent !important;
            box-shadow: none !important;
            border: none !important;
          }
        `}
      </style>
      <div style={styles.container}>
        <div style={styles.mainLayout}>
          {/* Left Sidebar */}
          <div style={styles.sidebar} className="sidebar-scroll">
            <div style={styles.sidebarHeader}>
              <h3 style={styles.sidebarTitle}>Make a Persona</h3>
              <div style={styles.progressBar}>
                <div style={{
                  ...styles.progressFill,
                  width: `${getProgressPercentage()}%`
                }}></div>
              </div>
              <div style={styles.progressText}>
                {getProgressPercentage()}% • {currentStepInfo.label} {currentStepInfo.number} of {STEPS.length}
              </div>
            </div>
            
            <div style={styles.stepsList}>
              {STEPS.map((step, index) => {
                const isActive = step.id === currentStep;
                const isCompleted = index < currentStepIndex;
                
                return (
                  <div
                    key={step.id}
                    style={{
                      ...styles.stepItem,
                      ...(isActive ? styles.stepItemActive : {}),
                      ...(isCompleted ? styles.stepItemCompleted : {})
                    }}
                  >
                    <div style={{
                      ...styles.stepNumber,
                      ...(isActive ? styles.stepNumberActive : {}),
                      ...(isCompleted ? styles.stepNumberCompleted : {})
                    }}>
                      {isCompleted ? '✓' : step.number}
                    </div>
                    <div style={styles.stepLabel}>{step.label}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Main Content Area */}
          <div style={styles.mainContent}>
            {/* Header */}
            <div style={styles.header}>
              <div style={styles.titleSection}>
                <div style={styles.headerLeft}>
                  <div style={styles.avatar}>
                    <img src="/assistant.png" alt="AIRA" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
                  </div>
                  <div>
                    <h2 style={styles.title}>AIRA</h2>
                  </div>
                </div>
                <div style={styles.headerRight}>
                  <div style={styles.headerIcon}>👤</div>
                  <div style={styles.headerIcon}>⚙️</div>
                  <div style={styles.languageButton}>English ▼</div>
                </div>
              </div>
            </div>

            {/* Content based on step */}
            <div ref={chatContainerRef} style={styles.contentArea} className="conversation-panel-scroll">
              {/* Step 1 & 2: Welcome and Basic Information - Show Chat */}
              {(currentStep === 'welcome' || currentStep === 'basic_info') && (
                <div style={styles.conversationPanel}>
                  {conversationRef.current.length === 0 && !transcript && !liveUserTranscript && (
                    <div style={styles.placeholder}>
                      <video
                        src="/wmremove-transformed.webm"
                        autoPlay
                        loop
                        muted
                        playsInline
                        style={{ width: '150px', height: '150px', objectFit: 'cover', marginBottom: '16px', borderRadius: '50%' }}
                      />
                      <div style={{ fontSize: '18px', fontWeight: '600', color: '#94a3b8', marginBottom: '8px' }}>
                        AI Assistant Initializing...
                      </div>
                      <div style={styles.placeholderSub}>
                        <span>🎤</span>
                        <span>Please allow microphone access when prompted</span>
                      </div>
                    </div>
                  )}

                  {conversationRef.current.map((msg, index) => (
                    <div
                      key={msg.id || index}
                      style={{
                        ...styles.message,
                        ...(msg.role === 'assistant' ? styles.aiMessage : styles.userMessage)
                      }}
                    >
                      <div style={{
                        ...styles.messageAvatar,
                        ...(msg.role === 'assistant' ? styles.aiAvatar : styles.userAvatar)
                      }}>
                        {msg.role === 'assistant' ? (
                          <img src="/assistant.png" alt="AI" style={styles.avatarImage} />
                        ) : (
                          <User size={20} />
                        )}
                      </div>
                      <div style={{
                        ...styles.messageBubble,
                        ...(msg.role === 'user' ? styles.userMessageBubble : {})
                      }}>
                        <div style={styles.messageContent}>{msg.text}</div>
                      </div>
                    </div>
                  ))}

                  {liveUserTranscript && (
                    <div style={{ ...styles.message, ...styles.userMessage }}>
                      <div style={{ ...styles.messageAvatar, ...styles.userAvatar }}>
                        <User size={20} />
                      </div>
                      <div style={{ ...styles.messageBubble, ...styles.userMessageBubble, opacity: 0.8 }}>
                        <div style={styles.messageContent}>{liveUserTranscript}</div>
                      </div>
                    </div>
                  )}

                  {(status === 'thinking' || status === 'speaking') && !transcript && conversationRef.current.length > 0 && (
                    <div style={{ ...styles.message, ...styles.aiMessage }}>
                      <div style={{ ...styles.messageAvatar, ...styles.aiAvatar }}>
                        <img src="/assistant.png" alt="AI" style={styles.avatarImage} />
                      </div>
                      <div style={styles.typingIndicator}>
                        <span style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%', animation: 'typingAnimation 1.4s ease-in-out infinite' }}></span>
                        <span style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%', animation: 'typingAnimation 1.4s ease-in-out 0.2s infinite' }}></span>
                        <span style={{ width: '8px', height: '8px', background: '#ffffff', borderRadius: '50%', animation: 'typingAnimation 1.4s ease-in-out 0.4s infinite' }}></span>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div style={styles.errorMessage}>
                      <div style={styles.errorIcon}>⚠️</div>
                      <div>{error}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Step 3: Collect Website */}
              {currentStep === 'collect_website' && (
                <div style={styles.stepContent}>
                  <div className="website-input-inline" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <WebsiteInput
                      organizerId={organizerId}
                      onComplete={(branding) => {
                        setExtractedBranding(branding);
                        setTimeout(() => setCurrentStep('brand_setup'), 500);
                      }}
                      onSkip={() => {
                        setTimeout(() => setCurrentStep('brand_setup'), 500);
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Step 4: Brand Setup */}
              {currentStep === 'brand_setup' && (
                <div style={styles.stepContent}>
                  <div className="branding-setup-inline conversation-panel-scroll" style={{ flex: 1, overflowY: 'auto', paddingBottom: '20px' }}>
                    <BrandingSetup 
                      organizerId={organizerId} 
                      initialBranding={extractedBranding}
                    />
                  </div>
                  <div style={styles.stepActions}>
                    <button
                      style={styles.skipButton}
                      onClick={() => {
                        setTimeout(() => setCurrentStep('voice_creation'), 300);
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.1)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      }}
                    >
                      Skip for now →
                    </button>
                    <button
                      style={styles.nextButton}
                      onClick={() => {
                        setTimeout(() => setCurrentStep('voice_creation'), 300);
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-2px)';
                        e.target.style.boxShadow = '0 6px 25px rgba(99, 102, 241, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 20px rgba(99, 102, 241, 0.3)';
                      }}
                    >
                      Continue to Voice Creation →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 5: Voice Creation - Placeholder */}
              {currentStep === 'voice_creation' && (
                <div style={styles.stepContent}>
                  <div style={styles.placeholderStep}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>🎙️</div>
                    <h2 style={styles.placeholderTitle}>Persona Voice Creation</h2>
                    <p style={styles.placeholderText}>
                      This step will be implemented soon. You can customize your persona's voice settings here.
                    </p>
                    <button
                      style={styles.nextButton}
                      onClick={() => setCurrentStep('send_emails')}
                    >
                      Continue to Next Step →
                    </button>
                  </div>
                </div>
              )}

              {/* Step 6: Send Welcome Emails - Placeholder */}
              {currentStep === 'send_emails' && (
                <div style={styles.stepContent}>
                  <div style={styles.placeholderStep}>
                    <div style={{ fontSize: '48px', marginBottom: '20px' }}>📧</div>
                    <h2 style={styles.placeholderTitle}>Send Welcome Emails</h2>
                    <p style={styles.placeholderText}>
                      This step will be implemented soon. You can configure and send welcome emails here.
                    </p>
                    <button
                      style={styles.nextButton}
                      onClick={() => navigate('/organizer/dashboard')}
                    >
                      Complete Onboarding →
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Text Input (shown conditionally) */}
            {showTextInput && (currentStep === 'welcome' || currentStep === 'basic_info') && (
              <div style={styles.textInputContainer}>
                <form onSubmit={handleTextInputSubmit} style={styles.textInputForm}>
                  <input
                    type="text"
                    value={textInputValue}
                    onChange={(e) => setTextInputValue(e.target.value)}
                    placeholder="Type message here"
                    style={styles.textInput}
                    onFocus={(e) => {
                      e.target.style.borderColor = 'rgba(99, 102, 241, 0.5)';
                      e.target.style.boxShadow = '0 0 0 3px rgba(99, 102, 241, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                      e.target.style.boxShadow = 'none';
                    }}
                    autoFocus
                  />
                  <button 
                    type="submit" 
                    style={styles.sendButton}
                    disabled={!textInputValue.trim()}
                    onMouseEnter={(e) => {
                      if (textInputValue.trim()) {
                        e.target.style.transform = 'scale(1.05)';
                        e.target.style.boxShadow = '0 4px 15px rgba(99, 102, 241, 0.4)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                  >
                    ➤
                  </button>
                </form>
              </div>
            )}

            {/* Status Bar */}
            {(currentStep === 'welcome' || currentStep === 'basic_info') && (
              <div style={styles.footer}>
                <div style={styles.statusText}>
                  {status === 'connecting' && <>🔗 Connecting to AI Assistant...</>}
                  {status === 'thinking' && <>🧠 AI is thinking...</>}
                  {status === 'listening' && <>🎤 Listening... Speak now</>}
                  {status === 'speaking' && <>🔊 AI is responding...</>}
                  {status === 'saving' && <>💾 Saving your preferences...</>}
                  {status === 'done' && <>✅ Step Complete!</>}
                  {status === 'error' && <>⚠️ Connection Error</>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}


const styles = {
  container: {
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    padding: '0',
    position: 'relative',
    overflow: 'hidden',
  },
  mainLayout: {
    display: 'flex',
    height: '100vh',
    maxWidth: '1600px',
    margin: '0 auto',
    overflow: 'hidden',
  },
  // Sidebar Styles
  sidebar: {
    width: '280px',
    background: 'rgba(15, 23, 42, 0.8)',
    backdropFilter: 'blur(20px)',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
  },
  sidebarHeader: {
    marginBottom: '32px',
  },
  sidebarTitle: {
    margin: '0 0 16px 0',
    color: '#f8fafc',
    fontSize: '18px',
    fontWeight: '700',
  },
  progressBar: {
    width: '100%',
    height: '6px',
    background: 'rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    overflow: 'hidden',
    marginBottom: '8px',
  },
  progressFill: {
    height: '100%',
    background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)',
    borderRadius: '10px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: '500',
  },
  stepsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '12px',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    color: '#94a3b8',
  },
  stepItemActive: {
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(139, 92, 246, 0.9) 100%)',
    color: '#ffffff',
    fontWeight: '600',
  },
  stepItemCompleted: {
    color: '#34d399',
  },
  stepNumber: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    background: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: '600',
    flexShrink: 0,
  },
  stepNumberActive: {
    background: 'rgba(255, 255, 255, 0.3)',
  },
  stepNumberCompleted: {
    background: 'rgba(16, 185, 129, 0.3)',
    color: '#34d399',
  },
  stepLabel: {
    fontSize: '14px',
    fontWeight: '500',
  },
  // Main Content Styles
  mainContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(30, 41, 59, 0.5)',
    backdropFilter: 'blur(20px)',
    height: '100%',
    overflow: 'hidden',
  },
  header: {
    padding: '20px 32px',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(15, 23, 42, 0.5)',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  avatar: {
    fontSize: '24px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: 0,
    color: '#f8fafc',
    fontSize: '20px',
    fontWeight: '600',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerIcon: {
    width: '36px',
    height: '36px',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    fontSize: '18px',
  },
  languageButton: {
    padding: '8px 16px',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(139, 92, 246, 0.2) 100%)',
    color: '#a5b4fc',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    border: '1px solid rgba(99, 102, 241, 0.3)',
  },
  contentArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px 32px',
    minHeight: 0,
  },
  conversationPanel: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    paddingBottom: '20px',
  },
  placeholder: {
    color: '#64748b',
    textAlign: 'center',
    padding: '60px 20px',
    fontSize: '16px',
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  placeholderSub: {
    fontSize: '14px',
    marginTop: '12px',
    color: '#475569',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  message: {
    display: 'flex',
    gap: '12px',
    maxWidth: '85%',
    animation: 'messageSlideIn 0.3s ease-out',
    marginBottom: '16px',
    alignItems: 'flex-start',
  },
  aiMessage: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
  },
  userMessage: {
    alignSelf: 'flex-end',
    flexDirection: 'row-reverse',
  },
  messageAvatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '4px',
    overflow: 'hidden',
  },
  aiAvatar: {
    background: 'rgba(99, 102, 241, 0.1)',
    border: '1px solid rgba(99, 102, 241, 0.2)',
  },
  userAvatar: {
    background: 'rgba(16, 185, 129, 0.1)',
    border: '1px solid rgba(16, 185, 129, 0.2)',
    color: '#10b981',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  messageBubble: {
    padding: '14px 18px',
    borderRadius: '18px',
    background: 'rgba(99, 102, 241, 0.2)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
  },
  userMessageBubble: {
    background: 'rgba(16, 185, 129, 0.2)',
    border: '1px solid rgba(16, 185, 129, 0.3)',
  },
  messageContent: {
    color: '#f8fafc',
    fontSize: '15px',
    lineHeight: '1.6',
    fontWeight: '400',
  },
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '14px 18px',
    background: 'rgba(99, 102, 241, 0.2)',
    borderRadius: '18px',
    maxWidth: '80px',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  errorMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    background: 'rgba(239, 68, 68, 0.15)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '12px',
    color: '#f87171',
    fontWeight: '500',
  },
  errorIcon: {
    fontSize: '20px',
  },
  // Step Content Styles
  stepContent: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  placeholderStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    textAlign: 'center',
    padding: '40px',
  },
  placeholderTitle: {
    color: '#f8fafc',
    fontSize: '24px',
    fontWeight: '700',
    marginBottom: '12px',
  },
  placeholderText: {
    color: '#94a3b8',
    fontSize: '16px',
    marginBottom: '32px',
    maxWidth: '500px',
  },
  stepActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'flex-end',
    marginTop: 'auto',
    paddingTop: '24px',
  },
  skipButton: {
    padding: '12px 24px',
    borderRadius: '10px',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '2px solid rgba(255, 255, 255, 0.2)',
    color: '#94a3b8',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  nextButton: {
    padding: '12px 24px',
    borderRadius: '10px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    border: 'none',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 20px rgba(99, 102, 241, 0.3)',
  },
  // Text Input Styles
  textInputContainer: {
    padding: '20px 32px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(15, 23, 42, 0.5)',
  },
  textInputForm: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  textInput: {
    flex: 1,
    padding: '12px 18px',
    borderRadius: '12px',
    background: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    color: '#f8fafc',
    fontSize: '15px',
    outline: 'none',
    transition: 'all 0.3s ease',
  },
  textInputFocus: {
    borderColor: 'rgba(99, 102, 241, 0.5)',
    boxShadow: '0 0 0 3px rgba(99, 102, 241, 0.1)',
  },
  sendButton: {
    padding: '12px 20px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
    border: 'none',
    color: '#ffffff',
    fontSize: '18px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    padding: '16px 32px',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    background: 'rgba(15, 23, 42, 0.5)',
    backdropFilter: 'blur(10px)',
  },
  statusText: {
    color: '#94a3b8',
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
};

export default OnboardingRealtime;
